import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Wallet, TrendingUp, TrendingDown, AlertCircle, RefreshCw, Filter } from "lucide-react";
import { motion } from "framer-motion";
import walletService from "../services/walletService";
import { formatError } from "../utils/errorHandler";
import { useTranslation, useFormat } from "../i18n";

const WalletPage = () => {
  const { t } = useTranslation("wallet");
  const fmt = useFormat();
  const [typeFilter, setTypeFilter] = useState("all"); // all, EARN, REDEEM, ADJUST
  const [page, setPage] = useState(0);
  const pageSize = 50;

  // Fetch wallet balance
  const { data: wallet, isLoading: walletLoading, error: walletError } = useQuery({
    queryKey: ["wallet"],
    queryFn: () => walletService.getMyWallet(),
  });

  // Fetch transactions
  const { data: transactionsData, isLoading: transactionsLoading, error: transactionsError, refetch } = useQuery({
    queryKey: ["transactions", page, typeFilter],
    queryFn: () => walletService.getMyTransactions(page * pageSize, pageSize),
  });

  const transactions = transactionsData || [];

  // Filter transactions by type (API returns type: "earn" | "redeem" | "adjust")
  const filteredTransactions = typeFilter === "all"
    ? transactions
    : transactions.filter(tx => tx.type === typeFilter);

  const loading = walletLoading || transactionsLoading;
  const error = walletError || transactionsError;

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return fmt.dateTime(dateString) || dateString;
  };

  // Signed amount ("+10", "-5", "0") in the active locale.
  const formatAmount = (amount) => fmt.number(amount, { signDisplay: "exceptZero" });

  const balance = wallet?.balance || 0;

  const typeLabels = {
    earn: t("transactions.types.earn"),
    redeem: t("transactions.types.redeem"),
    adjust: t("transactions.types.adjust"),
  };

  const filterOptions = [
    { value: "all", label: t("transactions.filters.all"), desktopLabel: t("transactions.filters.all_desktop") },
    { value: "earn", label: t("transactions.filters.earn"), desktopLabel: t("transactions.filters.earn") },
    { value: "redeem", label: t("transactions.filters.redeem"), desktopLabel: t("transactions.filters.redeem") },
    { value: "adjust", label: t("transactions.filters.adjust"), desktopLabel: t("transactions.filters.adjust_desktop") },
  ];

  return (
    <div className="min-h-screen bg-gray-50 pb-8 pt-3 sm:pt-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Current Balance Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="org-gradient rounded-2xl shadow-lg p-3 sm:p-8 mb-4 sm:mb-8 text-white"
        >
          {/* Mobile: icon + label + balance all on one row */}
          <div className="flex items-center gap-2 sm:hidden">
            <Wallet className="w-4 h-4 flex-shrink-0" />
            <p className="text-white/90 font-medium text-sm">{t("balance.title_mobile")}</p>
            {loading ? (
              <span className="text-sm">{t("common:status.loading")}</span>
            ) : error ? (
              <span className="text-lg font-bold ml-auto">--</span>
            ) : (
              <span className="text-lg font-bold ml-auto">{t("balance.points_short", { points: fmt.number(balance) })}</span>
            )}
          </div>

          {/* Desktop: full stacked layout */}
          <div className="hidden sm:block">
            <div className="flex items-center gap-2 mb-2">
              <Wallet className="w-6 h-6" />
              <p className="text-white/90 font-medium">{t("balance.title")}</p>
            </div>
            {loading ? (
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-white border-t-transparent"></div>
                <span className="text-lg">{t("common:status.loading")}</span>
              </div>
            ) : error ? (
              <div>
                <p className="text-2xl font-bold mb-2">--</p>
                <p className="text-sm text-white/80">{formatError(error)}</p>
                <button
                  onClick={() => refetch()}
                  className="mt-3 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-full text-sm font-medium transition-colors inline-flex items-center gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  {t("common:actions.retry")}
                </button>
              </div>
            ) : (
              <>
                <div className="text-5xl font-bold mb-2">
                  {t("balance.points_short", { points: fmt.number(balance) })}
                </div>
                <p className="text-white/80 text-sm">
                  {t("balance.description")}
                </p>
              </>
            )}
          </div>
        </motion.div>

        {/* Transaction History */}
        <div className="bg-white rounded-2xl shadow-md overflow-hidden transition-all duration-200 hover:shadow-xl hover:-translate-y-0.5">
          <div className="p-3 sm:p-6 border-b border-gray-200">
            <div className="flex items-center justify-between gap-2 mb-3 sm:mb-4">
              <div className="min-w-0">
                <h2 className="text-base sm:text-xl font-semibold text-gray-900">{t("transactions.title")}</h2>
                <p className="text-xs sm:text-sm text-gray-600 mt-0.5 sm:mt-1 truncate">{t("transactions.subtitle")}</p>
              </div>
              <button
                onClick={() => refetch()}
                className="p-1.5 sm:p-2 hover:bg-gray-100 rounded-2xl transition-colors flex-shrink-0"
                title={t("transactions.refresh")}
              >
                <RefreshCw className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
              </button>
            </div>

            {/* Transaction Type Filter */}
            <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
              <div className="flex items-center gap-1 mr-1 sm:mr-2">
                <Filter className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-500" />
                <span className="text-xs sm:text-sm font-medium text-gray-700">{t("transactions.filter_label")}</span>
              </div>
              {filterOptions.map((filter) => (
                <button
                  key={filter.value}
                  onClick={() => {
                    setTypeFilter(filter.value);
                    setPage(0);
                  }}
                  className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-medium transition-colors ${
                    typeFilter === filter.value
                      ? "org-gradient text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  <span className="sm:hidden">{filter.label}</span>
                  <span className="hidden sm:inline">{filter.desktopLabel}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="p-3 sm:p-6">
            {loading ? (
              <div className="flex justify-center items-center py-8 sm:py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              </div>
            ) : error ? (
              <div className="text-center py-8 sm:py-12">
                <AlertCircle className="w-10 h-10 sm:w-12 sm:h-12 text-red-500 mx-auto mb-3" />
                <p className="text-sm sm:text-base text-red-600">{t("transactions.load_failed")}</p>
                <p className="text-xs sm:text-sm text-red-500 mt-2">{formatError(error)}</p>
              </div>
            ) : !filteredTransactions || filteredTransactions.length === 0 ? (
              <div className="text-center py-8 sm:py-12">
                <Wallet className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-sm sm:text-base text-gray-500">
                  {typeFilter === "all" ? t("transactions.empty") : t(`transactions.empty_filtered.${typeFilter}`)}
                </p>
                <p className="text-xs sm:text-sm text-gray-400 mt-1">
                  {typeFilter === "all"
                    ? t("transactions.empty_hint")
                    : t("transactions.empty_filtered_hint")}
                </p>
              </div>
            ) : (
              <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("transactions.columns.type")}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("transactions.columns.description")}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("transactions.columns.amount")}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("transactions.columns.date")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredTransactions.map((transaction) => {
                      const typeConfig = {
                        earn: { icon: <TrendingUp className="w-4 h-4 text-green-600" />, label: typeLabels.earn, bg: "bg-green-100", text: "text-green-600" },
                        redeem: { icon: <TrendingDown className="w-4 h-4 text-red-600" />, label: typeLabels.redeem, bg: "bg-red-100", text: "text-red-600" },
                        adjust: { icon: <RefreshCw className="w-4 h-4 text-blue-600" />, label: typeLabels.adjust, bg: "bg-blue-100", text: "text-blue-600" },
                      };
                      const tc = typeConfig[transaction.type] || typeConfig.earn;

                      return (
                        <tr key={transaction.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <div className={`p-2 ${tc.bg} rounded-full`}>
                                {tc.icon}
                              </div>
                              <span className={`text-sm font-medium ${tc.text}`}>{tc.label}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">
                              {transaction.source || transaction.type}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`text-sm font-semibold ${
                              transaction.amount > 0 ? "text-green-600" : "text-red-600"
                            }`}>
                              {t("transactions.amount_points", { amount: formatAmount(transaction.amount) })}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(transaction.created_at)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Mobile transaction cards */}
            {!loading && !error && filteredTransactions && filteredTransactions.length > 0 && (
              <div className="md:hidden space-y-2">
                {filteredTransactions.map((transaction) => {
                  const typeConfig = {
                    earn: { icon: <TrendingUp className="w-3.5 h-3.5 text-green-600" />, label: typeLabels.earn, bg: "bg-green-100", text: "text-green-600" },
                    redeem: { icon: <TrendingDown className="w-3.5 h-3.5 text-red-600" />, label: typeLabels.redeem, bg: "bg-red-100", text: "text-red-600" },
                    adjust: { icon: <RefreshCw className="w-3.5 h-3.5 text-blue-600" />, label: typeLabels.adjust, bg: "bg-blue-100", text: "text-blue-600" },
                  };
                  const tc = typeConfig[transaction.type] || typeConfig.earn;

                  return (
                    <div key={transaction.id} className="border border-gray-200 rounded-2xl p-2.5 flex items-center gap-2">
                      <div className={`p-1.5 ${tc.bg} rounded-full flex-shrink-0`}>{tc.icon}</div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          <span className={`text-xs font-medium ${tc.text}`}>{tc.label}</span>
                          <span className="text-[10px] text-gray-400 truncate">· {transaction.source || transaction.type}</span>
                        </div>
                        <div className="text-[10px] text-gray-500 truncate">{formatDate(transaction.created_at)}</div>
                      </div>
                      <span className={`text-sm font-semibold flex-shrink-0 ${transaction.amount > 0 ? "text-green-600" : "text-red-600"}`}>
                        {formatAmount(transaction.amount)}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Pagination — shared across mobile and desktop */}
            {!loading && !error && filteredTransactions && filteredTransactions.length === pageSize && (
              <div className="flex items-center justify-between gap-2 mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-gray-200">
                <p className="text-[11px] sm:text-sm text-gray-600 truncate">
                  <span className="sm:hidden">
                    {t("transactions.pagination.range", { from: page * pageSize + 1, to: page * pageSize + filteredTransactions.length })}
                  </span>
                  <span className="hidden sm:inline">
                    {t("transactions.pagination.showing", { from: page * pageSize + 1, to: page * pageSize + filteredTransactions.length })}
                  </span>
                </p>
                <div className="flex gap-1.5 sm:gap-2 flex-shrink-0">
                  <button
                    onClick={() => setPage(Math.max(0, page - 1))}
                    disabled={page === 0}
                    className="px-3 sm:px-4 py-1.5 sm:py-2 border border-gray-300 rounded-full text-xs sm:text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t("transactions.pagination.previous")}
                  </button>
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={filteredTransactions.length < pageSize}
                    className="px-3 sm:px-4 py-1.5 sm:py-2 org-gradient text-white rounded-full text-xs sm:text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t("common:actions.next")}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WalletPage;
